import {
  BackgroundIcon,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  ConjoinedIcons,
  type IconName,
  Skeleton,
  Text,
} from "@prismicio/editor-ui"

export interface LinkDescriptionCardProps {
  url?: URL | string
  title?: string
  subTitle?: string
  description?: string
  largeIconName?: IconName
  loading?: boolean
  error?: boolean
  onEdit?: () => void
  onUnlink?: () => void
}

export function LinkDescriptionCard(props: LinkDescriptionCardProps) {
  const {
    description,
    largeIconName = "public",
    loading,
    error,
    onEdit,
    onUnlink,
  } = props

  const headerContent = error ? (
    <LinkDescriptionErrorContent />
  ) : (
    <LinkDescriptionCardBody
      {...props}
      largeIconName={largeIconName}
      loading={loading}
    />
  )

  return (
    <Card sx={{ width: 420 }}>
      <CardContent gap={12}>
        <Box
          display="grid"
          gridTemplateColumns="max-content 1fr min-content"
          gridTemplateRows="1fr"
          gap={8}
        >
          {headerContent}
          <ButtonGroup size="medium" color="grey" sx={{ justifySelf: "end" }}>
            {onEdit && <Button startIcon="edit" onClick={onEdit} />}
            {onUnlink && <Button startIcon="linkOff" onClick={onUnlink} />}
          </ButtonGroup>
        </Box>

        {!error && <Description description={description} loading={loading} />}
      </CardContent>
    </Card>
  )
}

interface TitleProps {
  url?: URL | string
  title?: string
  loading?: boolean
}

function Title(props: TitleProps) {
  const { url, title, loading } = props

  if (loading) return <LinkDescriptionCardSkeleton height={16} width={80} />
  if (!title) return null

  return (
    <Text variant="smallBold" href={url?.toString()}>
      {title}
    </Text>
  )
}

interface SubtitleProps {
  subTitle?: string
  loading?: boolean
}

function Subtitle(props: SubtitleProps) {
  const { subTitle, loading } = props

  if (loading)
    return (
      <Box padding={{ block: 2 }}>
        <LinkDescriptionCardSkeleton height={12} width={60} />
      </Box>
    )

  if (!subTitle) return null

  return (
    <Text variant="small" color="grey11" noWrap>
      {subTitle}
    </Text>
  )
}

interface DescriptionProps {
  description?: string
  loading?: boolean
}

function Description(props: DescriptionProps) {
  const { description, loading } = props

  if (!description || loading) return null

  return (
    <Text variant="small" color="grey11">
      {description}
    </Text>
  )
}

interface LinkDescriptionCardBodyProps {
  largeIconName: IconName
  title?: string
  subTitle?: string
  url?: string | URL
  loading?: boolean
}

function LinkDescriptionCardBody(props: LinkDescriptionCardBodyProps) {
  const { largeIconName, title, url, loading, subTitle } = props

  return (
    <>
      <ConjoinedIcons largeIconName={largeIconName} smallIconName="link" />
      <Box flexDirection="column" minWidth={0}>
        <Title title={title} url={url} loading={loading} />
        <Subtitle subTitle={subTitle} loading={loading} />
      </Box>
    </>
  )
}

function LinkDescriptionErrorContent() {
  return (
    <Box alignItems="center" gap={8}>
      <BackgroundIcon name="alert" color="tomato" size="small" />
      <Text color="tomato11">Failed to load linked document data</Text>
    </Box>
  )
}

function LinkDescriptionCardSkeleton(props: { width: number; height: number }) {
  const { width, height } = props

  return (
    <Skeleton color="grey5" borderRadius={6} width={width} height={height} />
  )
}
